import { format } from "date-fns";
import { formatDateAndTimeInALuxuryWay } from "./formatDates";
import {
  formatExchangeRate,
  formatFiat,
  formatSatoshis,
} from "./formatNumbers";
import { CurrencySnapshot } from "./types";
import { invert } from "lodash";
import { TransactionModelName } from "./transactionsList";
import { Bob } from "../stores/Bob";
import { makeTransactionPageDataForOurInvoice } from "./invoices";
import {
  makeTransactionPageDataForOnchainInbound,
  OnchainTransactionViewOutput,
} from "./onchainInboundTransactions";
import {
  makeTransactionPageDataForOnchainOutbound,
  OnchainOutboundTransactionViewOutput,
} from "./onchainOutboundTransactions";
import {
  makeTransactionPageDataForStablePayment,
  StablePaymentViewOutput,
} from "./stablePayments";
import { makeTransactionPageDataForPlatformCredit } from "./platformCredits";

export type SelectedTransactionToView = {
  modelName: TransactionModelName;
  uuid: string;
};

export type TransactionViewOutput = {
  header: string;
  satoshisString: string;
  fiatString: string;
  exchangeRateString: string;
  createdAtString: string;
  comment: string | null;
  invoice: string | null;
  routingFeeSatoshis: number | null;
  platformFeeSatoshis: number | null;
  routingFeeFiatString: string | null;
  platformFeeFiatString: string | null;
  id: string;
  lightningAddress: string | null;
};

export const makeTransactionsPage = ({
  header,
  uuid,
  createdAtInteger,
  currencySnapshot,
  currency,
  amountSatoshis,
  isCredit,
  comment,
  payRequest,
  routingFeeSatoshis,
  platformFeeSatoshis,
  lightningAddress,
}): TransactionViewOutput => {
  return {
    header,
    /// nice long form fomratting like June 12th, 2025, 6:37 PM, EST
    createdAtString: formatDateAndTimeInALuxuryWay(createdAtInteger),
    satoshisString: formatSatoshis(amountSatoshis, isCredit, "sat"),
    fiatString: formatFiat({
      satoshis: amountSatoshis,
      currency,
      currencySnapshot,
      isNegative: !isCredit,
    }),
    exchangeRateString: formatExchangeRate({ currency, currencySnapshot }),
    platformFeeSatoshis: platformFeeSatoshis,
    platformFeeFiatString: formatFiat({
      satoshis: platformFeeSatoshis,
      currency,
      currencySnapshot,
      isNegative: false,
    }),
    routingFeeSatoshis: routingFeeSatoshis,
    routingFeeFiatString: formatFiat({
      satoshis: routingFeeSatoshis,
      currency,
      currencySnapshot,
      isNegative: false,
    }),
    invoice: payRequest,
    comment: comment,
    id: uuid,
    lightningAddress: lightningAddress || null,
  };
};

export const getTransactionData = (
  bob: Bob,
):
  | TransactionViewOutput
  | OnchainTransactionViewOutput
  | OnchainOutboundTransactionViewOutput
  | StablePaymentViewOutput
  | null => {
  const { modelName, uuid } = bob.selectedTransactionToView.data;
  if (modelName === "OurInvoice") {
    const invoiceData = bob.ourInvoices.data.find((inv) => inv.uuid === uuid);
    return makeTransactionPageDataForOurInvoice(invoiceData, bob);
  }
  if (modelName == "PayAttempt") {
    const payAttempt = bob.payAttemptsPersisted.find(
      (attempt) => attempt.uuid === uuid,
    );
    return payAttempt.transactionPageData;
  }
  if (modelName === "PlatformCredit") {
    const creditData = bob.platformCredits.data.find(
      (credit) => credit.uuid === uuid,
    );
    return makeTransactionPageDataForPlatformCredit(creditData, bob);
  }
  if (modelName === "OnchainInboundTransaction") {
    const txData = bob.ourOnchainInboundTransactions.data.find(
      (tx) => tx.outpoint === uuid,
    );
    if (txData) {
      return makeTransactionPageDataForOnchainInbound(txData, bob);
    }
  }
  if (modelName === "OnchainOutboundTransaction") {
    const txData = bob.ourOnchainOutboundTransactions.data.find(
      (tx) => tx.uuid === uuid,
    );
    if (txData) {
      return makeTransactionPageDataForOnchainOutbound(txData, bob);
    }
  }
  if (modelName === "StablePayment") {
    const txData = bob.ourStablePayments.data.find((tx) => tx.uuid === uuid);
    if (txData) {
      return makeTransactionPageDataForStablePayment(txData, bob);
    }
  }
  return null;
};
